<!-- LoginModal.vue -->
<script setup>
import { ref, onMounted, watch } from 'vue' // Añadido watch para observar cambios en props
import { useAuthStore } from '../stores/auth'
import { useWebsiteConfigStore } from '../stores/website/configStore'
import LoadingView from '../components/LoadingView.vue'
import { useI18n } from 'vue-i18n'
import LanguageSwitcher from '../components/LanguageSwitcher.vue'

// Definir props para recibir el estado de registro exitoso
const props = defineProps({
  showRegistrationSuccess: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['login-success', 'close', 'switch-to-register'])

// Añade esta función
const switchToRegister = () => {
  emit('switch-to-register')
}

const authStore = useAuthStore()
const websiteStore = useWebsiteConfigStore()
const { t } = useI18n()

// Estados del login
const username = ref('')
const password = ref('')
const passwordVisible = ref(false)
const loading = ref(false)
const error = ref('')
const successMessage = ref('') // Para mensajes de éxito generales

// Observar cambios en showRegistrationSuccess para mostrar el mensaje
watch(() => props.showRegistrationSuccess, (newValue) => {
  if (newValue) {
    successMessage.value = t('auth.registrationSuccessful')
    // Establecer un temporizador para borrar el mensaje después de 5 segundos
    setTimeout(() => {
      successMessage.value = ''
    }, 5000)
  }
}, { immediate: true })

// Cargar la configuración cuando el componente se monta
onMounted(async () => {
  try {
    if (!websiteStore.config.branding) {
      await websiteStore.loadConfig()
    }
  } catch (err) {
    console.error('Error loading website config:', err)
  }
})

const togglePasswordVisibility = () => {
  passwordVisible.value = !passwordVisible.value
}

const handleLogin = async () => {
  try {
    loading.value = true;
    error.value = '';
    successMessage.value = ''; // Limpiar mensaje de éxito al iniciar login

    // Validaciones frontend
    if (!username.value.trim()) {
      error.value = t('auth.errors.emptyUsername');
      setTimeout(() => {
        error.value = '';
      }, 5000);
      return;
    }

    if (!password.value.trim()) {
      error.value = t('auth.errors.emptyPassword');
      setTimeout(() => {
        error.value = '';
      }, 5000);
      return;
    }

    // Primera llamada al login
    const result = await authStore.login({
      username: username.value,
      password: password.value
    }, t);

    console.log('Resultado del login:', result);

    // Si hay una sesión activa, mostrar mensaje y detener el proceso
    if (result.isActiveSession) {
      console.log('Sesión activa detectada - primer intento');
      error.value = result.message;
      loading.value = false;
      // Guardar estado para saber que es el primer intento
      loginAttempt.value = 'first';
      // Limpiar mensaje después de 5 segundos
      setTimeout(() => {
        error.value = '';
      }, 5000);
      return;
    }

    // Si es un segundo intento después de sesión activa
    if (loginAttempt.value === 'first') {
      console.log('Segundo intento después de sesión activa');
      loginAttempt.value = 'second';
    }

    // Si el login fue exitoso
    if (result.success) {
      emit('login-success');
      username.value = '';
      password.value = '';
      loginAttempt.value = null;
    }

  } catch (err) {
    console.log('Error en handleLogin:', err);
    error.value = err.message;
    setTimeout(() => {
        error.value = '';
      }, 5000);
  } finally {
    loading.value = false;
  }
};

// Agregar estado para trackear intentos de login
const loginAttempt = ref(null);
</script>
<template>
  <LoadingView v-if="loading" />

  <div v-else 
    class="login-background"
    :style="websiteStore.config.branding?.background ? 
      `background-image: url(${websiteStore.config.branding.background})` : ''"
  >
    <div class="content-wrapper">
      <div class="logo-container">
        <img 
          v-if="websiteStore.config.branding?.logo_desktop"
          :src="websiteStore.config.branding.logo_desktop"
          class="logo"
        />
      </div>
      <div class="login-form">
        <!-- Mensaje de éxito para registro completado -->
        <div v-if="successMessage" class="message success-message">
          {{ successMessage }}
        </div>
        
        <input 
          type="text"
          v-model="username"
          :placeholder="t('auth.usernamePlaceholder')"
          class="neon-input"
        />
        <div class="password-container">
          <input 
            :type="passwordVisible ? 'text' : 'password'"
            v-model="password"
            :placeholder="t('auth.passwordPlaceholder')"
            class="neon-input"
          />
          <button 
            type="button"
            @click="togglePasswordVisibility"
            class="toggle-password"
          >
            <font-awesome-icon 
              :icon="passwordVisible ? 'eye-slash' : 'eye'"
              class="password-icon"
            />
          </button>
        </div>
        <button @click="handleLogin" class="neon-button">
          {{ t('auth.login') }}
        </button>
        <div class="links-row">
          <a href="#" @click.prevent="switchToRegister" class="link">{{ t('auth.register') }}</a>
          <!-- <a href="#" class="link">{{ t('auth.forgotPassword') }}</a> -->
        </div>
        <LanguageSwitcher />
        
        <!-- Mensaje de error -->
        <div v-if="error" class="error-container">
          {{ error }}
        </div>
        
        
      </div>
    </div>
  </div>
</template>
<style scoped>
@import '../assets/styles/online/OnlineMain.css';
@import '../assets/styles/main.css';

/* Estilos base para los mensajes */


/* Mensaje de éxito con estilo neon */
.success-message {
  background-color: rgba(16, 185, 129, 0.2); /* Verde semitransparente */
  color: #10b981; /* Verde */
  border: 1px solid #10b981;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.4);
  border-radius: 8px;
  padding: 15px;
  margin: 20px auto;
  max-width: 600px;
  text-align: center;
}

/* Mensaje de error con estilo neon */
.error-container {
  background-color: rgba(239, 68, 68, 0.2);
  color: rgb(239, 68, 68);
  border: 1px solid rgb(239, 68, 68);
  border-radius: 8px;
  padding: 15px;
  margin: 20px auto;
  max-width: 600px;
  text-align: center;
}


</style>